import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { validateEmail } from "../../utils/validationUtils";
import { checkUser, forgotPasswordWithBackend } from "../../api/loginAPI";
import { routes } from "../../routes/routes";
import { getOrganizationId } from "../../utils/orgIdUtils";
import MaltoraLogo from "../landing/MaltoraLogo";

const inputStyle: React.CSSProperties = {
  width: "100%",
  height: "46px",
  border: "1.5px solid rgba(15,23,42,0.14)",
  borderRadius: "8px",
  padding: "0 14px 0 42px",
  fontFamily: "var(--font-ui, DM Sans, system-ui, sans-serif)",
  fontSize: "14px",
  color: "#0F172A",
  background: "#F8F9FA",
  outline: "none",
  boxSizing: "border-box",
  transition: "border-color 0.15s",
};

const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      setError("Please enter your email address.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Enter a valid email address.");
      return;
    }

    try {
      const userCheck = await checkUser({ email });
      if (!userCheck.exists) {
        setError("Email is not registered.");
        return;
      }
      await forgotPasswordWithBackend(email);
      setError("");
      setSubmitted(true);
    } catch (err) {
      console.error("Error during forgot password flow", err);
      setError("An error occurred. Please try again.");
    }
  };

  return (
    <div
      style={{
        display: "flex",
        minHeight: "100vh",
        fontFamily: "var(--font-ui, DM Sans, system-ui, sans-serif)",
      }}
    >
      {/* LEFT */}
      <div
        style={{
          width: "420px",
          flexShrink: 0,
          background: "linear-gradient(160deg,#0F2D2B 0%,#1F5F5B 100%)",
          display: "flex",
          flexDirection: "column",
          padding: "48px",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            bottom: "-80px",
            right: "-80px",
            width: "300px",
            height: "300px",
            borderRadius: "50%",
            background: "rgba(255,255,255,0.03)",
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            position: "absolute",
            top: "-40px",
            left: "-40px",
            width: "200px",
            height: "200px",
            borderRadius: "50%",
            background: "rgba(255,255,255,0.02)",
            pointerEvents: "none",
          }}
        />
        <Link
          to="/"
          style={{
            display: "block",
            width: "110px",
            marginBottom: "60px",
            flexShrink: 0,
          }}
        >
          <MaltoraLogo fill="#fff" />
        </Link>
        <div style={{ marginTop: "auto", marginBottom: "auto" }}>
          <div
            style={{
              width: "52px",
              height: "52px",
              borderRadius: "14px",
              background: "rgba(255,255,255,0.1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: "28px",
            }}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="rgba(255,255,255,0.85)"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="3" y="11" width="18" height="11" rx="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          </div>
          <h2
            style={{
              fontFamily:
                "var(--font-display, Satoshi, DM Sans, system-ui, sans-serif)",
              fontSize: "24px",
              fontWeight: "700",
              color: "#fff",
              lineHeight: "1.35",
              letterSpacing: "-0.02em",
              marginBottom: "14px",
            }}
          >
            Secure account recovery
          </h2>
          <p
            style={{
              fontSize: "14px",
              color: "rgba(255,255,255,0.6)",
              lineHeight: "1.7",
              marginBottom: "32px",
            }}
          >
            We&apos;ll send a reset link to your registered email. The link
            expires in 30 minutes.
          </p>
          <div
            style={{ display: "flex", flexDirection: "column", gap: "16px" }}
          >
            {[
              {
                icon: (
                  <>
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.6 3.37 2 2 0 0 1 3.57 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.73a16 16 0 0 0 6 6l.92-.92a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                  </>
                ),
                text: "Check your spam folder if the email doesn&apos;t arrive within a few minutes.",
              },
              {
                icon: (
                  <>
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </>
                ),
                text: "If you no longer have access to your email, contact support@maltora.app.",
              },
            ].map((item, i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  gap: "12px",
                  alignItems: "flex-start",
                }}
              >
                <div
                  style={{
                    width: "32px",
                    height: "32px",
                    borderRadius: "8px",
                    background: "rgba(255,255,255,0.08)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="rgba(255,255,255,0.7)"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    {item.icon}
                  </svg>
                </div>
                <p
                  style={{
                    fontSize: "13px",
                    color: "rgba(255,255,255,0.55)",
                    lineHeight: "1.6",
                    margin: 0,
                  }}
                >
                  {item.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* RIGHT */}
      <div
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#fff",
          padding: "40px 24px",
        }}
      >
        <div style={{ width: "100%", maxWidth: "380px" }}>
          {submitted ? (
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  width: "68px",
                  height: "68px",
                  borderRadius: "50%",
                  background: "rgba(31,95,91,0.1)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 24px",
                }}
              >
                <svg
                  width="30"
                  height="30"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#1F5F5B"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
              </div>
              <h1
                style={{
                  fontFamily:
                    "var(--font-display, Satoshi, DM Sans, system-ui, sans-serif)",
                  fontSize: "26px",
                  fontWeight: "700",
                  color: "#0F172A",
                  marginBottom: "10px",
                  letterSpacing: "-0.02em",
                }}
              >
                Check your inbox
              </h1>
              <p
                style={{
                  fontSize: "14px",
                  color: "#64748B",
                  lineHeight: "1.7",
                  marginBottom: "8px",
                }}
              >
                We&apos;ve sent a password reset link to
              </p>
              <p
                style={{
                  fontSize: "15px",
                  fontWeight: "600",
                  color: "#0F172A",
                  marginBottom: "32px",
                }}
              >
                {email}
              </p>
              <p
                style={{
                  fontSize: "13px",
                  color: "#64748B",
                  marginBottom: "24px",
                  lineHeight: "1.6",
                }}
              >
                Didn&apos;t receive it? Check your spam folder or{" "}
                <button
                  onClick={() => setSubmitted(false)}
                  style={{
                    background: "none",
                    border: "none",
                    color: "#1F5F5B",
                    fontFamily:
                      "var(--font-ui, DM Sans, system-ui, sans-serif)",
                    fontSize: "13px",
                    fontWeight: "600",
                    cursor: "pointer",
                    padding: 0,
                  }}
                >
                  try again
                </button>
                .
              </p>
              <Link
                to={`/${routes.public.signIn}`}
                state={{ fromApp: true }}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#64748B",
                  textDecoration: "none",
                }}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="15 18 9 12 15 6" />
                </svg>
                Back to sign in
              </Link>
            </div>
          ) : (
            <>
              <Link
                to={`/${routes.public.signIn}`}
                state={{ fromApp: true }}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  fontSize: "13px",
                  color: "#64748B",
                  textDecoration: "none",
                  marginBottom: "32px",
                }}
              >
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="15 18 9 12 15 6" />
                </svg>
                Back to sign in
              </Link>
              <h1
                style={{
                  fontFamily:
                    "var(--font-display, Satoshi, DM Sans, system-ui, sans-serif)",
                  fontSize: "28px",
                  fontWeight: "700",
                  color: "#0F172A",
                  marginBottom: "8px",
                  letterSpacing: "-0.02em",
                }}
              >
                Forgot password?
              </h1>
              <p
                style={{
                  fontSize: "14px",
                  color: "#64748B",
                  marginBottom: "32px",
                  lineHeight: "1.6",
                }}
              >
                Enter your work email and we&apos;ll send you a link to reset
                your password.
              </p>
              <form onSubmit={handleSubmit} noValidate>
                <div style={{ marginBottom: "20px" }}>
                  <label
                    htmlFor="email"
                    style={{
                      display: "block",
                      fontSize: "13px",
                      fontWeight: "600",
                      color: "#0F172A",
                      marginBottom: "6px",
                    }}
                  >
                    Work email
                  </label>
                  <div style={{ position: "relative" }}>
                    <span
                      style={{
                        position: "absolute",
                        left: "14px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        color: "#64748B",
                        display: "flex",
                      }}
                    >
                      <svg
                        width="15"
                        height="15"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                        <polyline points="22,6 12,13 2,6" />
                      </svg>
                    </span>
                    <input
                      id="email"
                      type="email"
                      placeholder="you@company.com"
                      autoComplete="email"
                      style={{
                        ...inputStyle,
                        borderColor: error ? "#ef4444" : "rgba(15,23,42,0.14)",
                      }}
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setError("");
                      }}
                      onFocus={(e) => (e.target.style.borderColor = "#1F5F5B")}
                      onBlur={(e) =>
                        (e.target.style.borderColor = error
                          ? "#ef4444"
                          : "rgba(15,23,42,0.14)")
                      }
                    />
                  </div>
                  {error && (
                    <span
                      style={{
                        fontSize: "12px",
                        color: "#ef4444",
                        marginTop: "4px",
                        display: "block",
                      }}
                    >
                      {error}
                    </span>
                  )}
                </div>
                <button
                  type="submit"
                  style={{
                    width: "100%",
                    height: "48px",
                    background: "#E86A0D",
                    color: "#fff",
                    fontFamily:
                      "var(--font-ui, DM Sans, system-ui, sans-serif)",
                    fontSize: "15px",
                    fontWeight: "700",
                    border: "none",
                    borderRadius: "10px",
                    cursor: "pointer",
                    letterSpacing: "0.01em",
                    transition: "background 0.15s",
                    marginBottom: "16px",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = "#d45e0b")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background = "#E86A0D")
                  }
                >
                  Send reset link
                </button>
                <p
                  style={{
                    fontSize: "12px",
                    color: "#64748B",
                    textAlign: "center",
                    lineHeight: "1.5",
                  }}
                >
                  Remember your password?{" "}
                  <Link
                    to={`/${routes.public.signIn}`}
                    state={{ fromApp: true }}
                    style={{
                      color: "#1F5F5B",
                      fontSize: "12px",
                      fontWeight: "600",
                      textDecoration: "none",
                    }}
                  >
                    Sign in
                  </Link>
                </p>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
